import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { Fragment, useEffect, useState } from 'react'
import {
  createFairValueInstrument,
  deleteFairValueInstrument,
  getHedgingFairValue,
  updateFairValueInstrument,
} from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, thousandSeperator } from 'utils'

import { EditFairValueInstrumentDialog } from './EditFairValueInstrumentDialog'
import type { IFairValueInstrument, IHedgingSection } from './types'

const formatAmount = (value?: number | null) => {
  if (value == null || (!value && value !== 0)) return '-'
  return `$${thousandSeperator(value)}`
}

export const HedgingFairValue = () => {
  const [isLoading, setLoading] = useState(false)
  const [sections, setSections] = useState<IHedgingSection[]>([])
  const [editItem, setEditItem] = useState<Partial<IFairValueInstrument> | null>(null)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getHedgingFairValue()
      setSections(result.sections || [])
    } catch (error) {
      console.error(error)
      setSections([])
    }
    setLoading(false)
  }

  const onSave = async (data: Partial<IFairValueInstrument>) => {
    if (editItem?.id) await updateFairValueInstrument(editItem.id, data)
    else await createFairValueInstrument(data)
    setEditItem(null)
    fetch()
  }

  const onDelete = async (item: IFairValueInstrument) => {
    const ok = await confirm(`Delete "${item.instrument}"?`)
    if (!ok) return
    setLoading(true)
    await deleteFairValueInstrument(item.id)
    fetch()
  }

  const hasRows = sections.some((section) => section.rows.length > 0)

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Hedging &amp; Fair Value
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
        <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditItem({})}>
          <PlusIcon className="w-4 h-4" /> Add
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        IRLCs and forward/TBA positions at fair value (ASC 815); MLHS fair-value-option mark (ASC 825); realized
        pair-offs. Each line&apos;s Fair Value ties to its GL. GL Balance pulls from the Trial Balance.
      </p>

      <div className="relative overflow-x-auto">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-700 min-w-max">
          <thead className="text-xs uppercase bg-gray-100">
            <tr>
              {['Instrument', 'Reference / Loan', 'Notional', 'FV GL Acct', 'Fair Value', 'GL Balance', 'Variance', 'Action'].map(
                (h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {!hasRows && !isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                  No fair value instruments yet.
                </td>
              </tr>
            )}
            {sections.map((section) => (
              <Fragment key={section.key}>
                <tr key={`header-${section.key}`} className="bg-slate-700 text-white">
                  <td colSpan={8} className="px-3 py-2 font-semibold text-sm">
                    {section.label}
                  </td>
                </tr>
                {section.rows.length === 0 && (
                  <tr key={`empty-${section.key}`}>
                    <td colSpan={8} className="px-3 py-3 text-gray-400 italic">
                      No instruments in this section.
                    </td>
                  </tr>
                )}
                {section.rows.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{item.instrument}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{item.reference || '-'}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(item.notional)}</td>
                    <td className="px-3 py-2">{item.fvGlAccountNo}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(item.fairValue)}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(item.glBalance)}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(item.variance)}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2 items-center">
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => setEditItem(item)}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                        <span
                          className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onDelete(item)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {section.subtotal && (
                  <tr key={`subtotal-${section.key}`} className="bg-blue-50 font-semibold border-t">
                    <td colSpan={4} className="px-3 py-2">
                      Derivative subtotal
                    </td>
                    <td className="px-3 py-2 text-right">{formatAmount(section.subtotal.fairValue)}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(section.subtotal.glBalance)}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(section.subtotal.variance)}</td>
                    <td />
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {editItem && (
        <EditFairValueInstrumentDialog item={editItem} onClose={() => setEditItem(null)} onSave={onSave} />
      )}
    </div>
  )
}
